import { useState } from 'react'
import { useIntl } from 'react-intl'

import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { isSessionError } from '@/api/mutation/useOnrApi'
import {
  PREANALYSIS_CONTRARY_DECISION_TODAY_CODE,
  PREANALYSIS_STATUS_CHANGED_CODE,
  type UpdateJudicialPreanalysisVariables,
  useUpdateJudicialPreanalysis,
} from '@/api/useUpdateJudicialPreanalysis'
import { type JudicialPreanalysis } from '@/components/model'
import { toast } from '@/components/molecules/Toaster'
import { type JudicialPreanalysisListViewProps } from '@/components/templates/JudicialPreanalysisListView'
import { type JudicialPreanalysisFilter } from '@/routes/judicial.index'
import { judicialPreanalysesQuery } from '@/state/judicial/judicialPreanalyses'

const hasCode = (error: unknown, code: string) =>
  typeof error === 'object' && error !== null && 'code' in error && error.code === code

const errorMessageId = (error: unknown) => {
  if (hasCode(error, PREANALYSIS_STATUS_CHANGED_CODE)) {
    return 'judicial.preanalysis.error.statusChanged'
  }
  if (hasCode(error, PREANALYSIS_CONTRARY_DECISION_TODAY_CODE)) {
    return 'judicial.preanalysis.error.contraryDecisionToday'
  }
  return 'judicial.preanalysis.error.generic'
}

export const useJudicialPreanalysisList = (): JudicialPreanalysisListViewProps => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { preanalysisFilter } = useSearch({ from: '/judicial/' })
  const { data } = useQuery(judicialPreanalysesQuery())
  const { mutateAsync } = useUpdateJudicialPreanalysis()
  const [pendingPersonCode, setPendingPersonCode] = useState<string | null>(null)

  const update = async (variables: UpdateJudicialPreanalysisVariables) => {
    setPendingPersonCode(variables.personCode)
    try {
      await mutateAsync(variables)
    } catch (error) {
      if (isSessionError(error)) {
        throw error
      }
      toast({
        title: intl.formatMessage({ id: errorMessageId(error) }),
        variant: 'destructive',
      })
      throw error
    } finally {
      setPendingPersonCode(null)
    }
  }

  const act =
    (action: 'start' | 'authorize' | 'complete') =>
    async ({ personCode, status }: JudicialPreanalysis) => {
      await update({ action, personCode, expectedStatus: status ?? 'new' }).catch(() => {})
    }

  return {
    preanalyses: data?.data ?? [],
    selectedFilter: preanalysisFilter,
    onFilterChange: async (filter: JudicialPreanalysisFilter) => {
      await navigate({ to: '/judicial', search: (previous) => ({ ...previous, preanalysisFilter: filter }) })
    },
    pendingPersonCode,
    onStart: act('start'),
    onAuthorize: act('authorize'),
    onComplete: act('complete'),
    onRefuse: async ({ personCode, status }, note) =>
      update({ action: 'refuse', personCode, expectedStatus: status ?? 'new', note }),
    onNoteChange: async ({ personCode, status }, note) =>
      update({ action: 'note', personCode, expectedStatus: status ?? 'new', note }),
  }
}
